import { useAtomValue } from 'jotai';
import { Heading, Text } from '@wix/design-system';
import React from 'react';
import { Confirm } from '@wix/wix-ui-icons-common';
import { currentStepAtom, stepsAtom } from '../../context/progressBarAtoms';
import cs from 'classnames';
import s from './StepsList.scss';
import { useTranslation } from '@wix/wix-i18n-config';
import { CardLayout } from '../../../layouts';
import { LoadingDots } from '../../../ProductLoadingGrid/components/LoadingDots/LoadingDots';

export interface Step {
  icon: React.ReactNode;
  label: string;
  description?: string;
  color?: string;
}

interface StepItemProps {
  step: Step;
  status: 'completed' | 'active' | 'pending';
  index: number;
}

const StepItem: React.FC<StepItemProps> = ({ step, status }) => {
  const { t } = useTranslation();

  const iconStyle =
    status === 'active' && step.color
      ? {
          backgroundColor: step.color,
          boxShadow: `0 4px 12px ${step.color}33`,
        }
      : undefined;

  return (
    <div className={cs(s['steps-item'], s[`steps-item--${status}`])}>
      <div className={cs(s.step__icon, s[`step__icon--${status}`])} style={iconStyle}>
        {status === 'completed' ? <Confirm /> : step.icon}
      </div>

      <div className={s.step__content}>
        <Text className={cs(s.step__label, s[`step__label--${status}`])}>{t(step.label)}</Text>

        <div className={s.step__descriptionContainer}>
          <Text className={cs(s.step__description, s[`step__description--${status}`])}>
            {t(step.description)}
          </Text>
          {status === 'active' && <LoadingDots />}
        </div>
      </div>
    </div>
  );
};

export const StepsList: React.FC = () => {
  const { t } = useTranslation();
  const currentStep = useAtomValue(currentStepAtom);
  const steps = useAtomValue(stepsAtom);

  if (!steps?.length) {
    return null;
  }

  return (
    <CardLayout gap={3}>
      <Heading className={s.heading}>{t('steps-list.title')}</Heading>

      {steps.map((step, index) => {
        let status: 'completed' | 'active' | 'pending';

        if (index < currentStep) {
          status = 'completed';
        } else if (index === currentStep) {
          status = 'active';
        } else {
          status = 'pending';
        }

        return <StepItem key={index} step={step} status={status} index={index} />;
      })}
    </CardLayout>
  );
};
